// /compare-plans — three-column comparison of Free, Premium, Family.
// Linked from Account upsell + Family upsell. Buy buttons forward to
// the OTCHealthMart Shopify checkout, which is where Stripe is wired.
//
// Focus group #11 (post-round-1): Jennifer, Linda, Margaret all flagged
// that "Compare plans" was a dead link before. Linda specifically called
// it out as "money left on the table."

import { Link } from 'react-router-dom';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { useMe } from '../hooks/useMe';
import { AppPage } from '../layouts/AppPage';

type Tier = 'FREE' | 'PREMIUM' | 'FAMILY';

interface Row {
  feature: string;
  free: string | boolean;
  premium: string | boolean;
  family: string | boolean;
}

const ROWS: Row[] = [
  { feature: 'Medicine list', free: true, premium: true, family: true },
  {
    feature: 'Safety alerts (Beers, STOPP/START, CMS HRM)',
    free: true,
    premium: true,
    family: true,
  },
  { feature: 'Interactions graph', free: true, premium: true, family: true },
  { feature: 'Doctor visit summary (PDF)', free: true, premium: true, family: true },
  { feature: 'Wallet card + TSA travel letter', free: true, premium: true, family: true },
  { feature: 'Read-aloud + listen to flags', free: true, premium: true, family: true },
  { feature: 'Ask Siggy', free: '5 chats / week', premium: 'Unlimited', family: 'Unlimited' },
  {
    feature: 'Pharmacist video visit (Dr. Marlee Grounds)',
    free: false,
    premium: '1 per month',
    family: '1 per month',
  },
  { feature: 'Family seats', free: '1 free', premium: '1 free', family: 'Up to 6' },
  {
    feature: 'Managed subjects (caregiver mode)',
    free: '1 (you + 1 loved one)',
    premium: '1 (you + 1 loved one)',
    family: 'Up to 6',
  },
  { feature: 'Caregiver read-only view', free: false, premium: false, family: true },
  { feature: 'Monthly family digest email', free: false, premium: false, family: true },
];

const PRICE: Record<Tier, { monthly: string; annual: string; sub: string }> = {
  FREE: { monthly: '$0', annual: '', sub: 'Always free.' },
  PREMIUM: { monthly: '$9.99/mo', annual: '$99/yr', sub: 'Save $20 a year on annual.' },
  FAMILY: { monthly: '$14.99/mo', annual: '$149/yr', sub: 'Save $30 a year on annual.' },
};

function renderCell(v: string | boolean): JSX.Element {
  if (v === true) {
    return (
      <span
        aria-label="Included"
        style={{ color: 'var(--color-accent)', fontSize: 20, fontWeight: 700 }}
      >
        ✓
      </span>
    );
  }
  if (v === false) {
    return (
      <span aria-label="Not included" style={{ color: 'var(--color-text-tertiary)', fontSize: 20 }}>
        no
      </span>
    );
  }
  return <span style={{ fontSize: 14 }}>{v}</span>;
}

export function ComparePlans(): JSX.Element {
  const { me } = useMe();
  const currentTier = (me?.tier ?? 'FREE') as Tier;

  return (
    <AppPage title="Compare plans" back={{ to: '/account', label: 'Account' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Pick what fits</p>
          <p className="food-intro__text">
            Every plan keeps medicine safety free. Upgrade only if you want unlimited Siggy chats or
            a real pharmacist visit. You can cancel any time on the Account page.
          </p>
        </div>
      </section>

      <section
        className="account-section"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}
      >
        {(['FREE', 'PREMIUM', 'FAMILY'] as Tier[]).map((t) => (
          <div
            key={t}
            style={{
              padding: 16,
              border: `2px solid ${currentTier === t ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-primary)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 18 }}>
              {t === 'FREE' ? 'Free' : t === 'PREMIUM' ? 'Premium' : 'Family'}
            </h3>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{PRICE[t].monthly}</p>
            {PRICE[t].annual ? (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)' }}>
                or {PRICE[t].annual}
              </p>
            ) : null}
            <p style={{ margin: '4px 0', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {PRICE[t].sub}
            </p>
            {currentTier === t ? (
              <span className="status-pill status-pill--active" style={{ alignSelf: 'flex-start' }}>
                Current plan
              </span>
            ) : t === 'FREE' ? (
              <span style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                Downgrade in Account
              </span>
            ) : (
              <a
                href={`https://www.otchealthmart.com/pages/medreview-plans?tier=${t.toLowerCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="home-hero__cta"
                style={{ alignSelf: 'flex-start' }}
              >
                Start {t === 'PREMIUM' ? 'Premium' : 'Family'}
              </a>
            )}
          </div>
        ))}
      </section>

      <section className="account-section" style={{ overflowX: 'auto' }}>
        <h3 className="account-section__title">What's included</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
          <thead>
            <tr style={{ borderBottom: '1.5px solid var(--color-border)' }}>
              <th style={{ textAlign: 'left', padding: '10px 6px', fontWeight: 600 }}>Feature</th>
              <th style={{ padding: '10px 6px', fontWeight: 600 }}>Free</th>
              <th style={{ padding: '10px 6px', fontWeight: 600 }}>Premium</th>
              <th style={{ padding: '10px 6px', fontWeight: 600 }}>Family</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.feature} style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                <td style={{ padding: '10px 6px', textAlign: 'left' }}>{r.feature}</td>
                <td style={{ padding: '10px 6px', textAlign: 'center' }}>{renderCell(r.free)}</td>
                <td style={{ padding: '10px 6px', textAlign: 'center' }}>
                  {renderCell(r.premium)}
                </td>
                <td style={{ padding: '10px 6px', textAlign: 'center' }}>{renderCell(r.family)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Coming this summer</h3>
        <div
          style={{
            padding: 16,
            border: '1.5px dashed var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-secondary)',
          }}
        >
          <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Caregiver plan. $11.99/mo or $119/yr.
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 15 }}>
            Everything in Premium plus read-only Family view for one parent or loved one. For people
            caring for just one person, where the full Family plan is more than you need. Tell us
            you want it and we'll let you know when it lands.
          </p>
          <a
            href="mailto:matthew@otchealthmart.com?subject=Caregiver%20plan%20interest"
            className="account-link"
            style={{ display: 'inline-block', marginTop: 8, fontWeight: 600 }}
          >
            Tell us
          </a>
        </div>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Why MedReview</h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-primary)', margin: '0 0 8px' }}>
          Safety checks stay free for everyone. We make money on the pharmacist visit and the family
          share plan, not on your list.
        </p>
        <Link to="/methodology" className="account-link" style={{ fontWeight: 600 }}>
          How we grade safety alerts.
        </Link>
      </section>
    </AppPage>
  );
}
